import * as R from 'ramda'

import {
  type GroupedLineItemArrangementInput,
  LineItemType,
  type RetailOrderLineItem,
} from '~/api/graphql/generated/types'
import { editRetailOrderLineItem } from '~/store/actions/finance'
import { editChargeSheetItem } from '~/store/duck/clientFinanceData'
import type { InvoiceLineItem } from '~/types'
import {
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
} from '~/utils/invoiceUtils'

/**
 * Creates delete items array for a bundle's children
 * @param bundleItem - The bundle invoice line item
 * @returns Array of delete items with id and expectedModificationDate
 */
export const createDeleteItemsForBundle = (
  bundleItem: InvoiceLineItem,
): Array<{ expectedModificationDate: string; id: string }> => {
  if (!bundleItem.items) return []

  return bundleItem.items.map((childItem) => {
    const isRetailChild = getIsRetailOrderLineItem(childItem)
    return {
      id: childItem.id,
      expectedModificationDate: isRetailChild
        ? (childItem as RetailOrderLineItem).modificationDate
        : (childItem as InvoiceLineItem).modificationDate,
    }
  })
}

/**
 * Separates items into active and declined sections
 * For bundles with mixed children: splits into two - one for active section, one for declined
 * @param items - Array of invoice or retail order line items
 * @returns Object with activeItems and declinedItems arrays
 */
export const separateItemsByDeclinedStatus = (
  items: (InvoiceLineItem | RetailOrderLineItem)[],
): {
  activeItems: (InvoiceLineItem | RetailOrderLineItem)[]
  declinedItems: (InvoiceLineItem | RetailOrderLineItem)[]
} => {
  const active: (InvoiceLineItem | RetailOrderLineItem)[] = []
  const declined: (InvoiceLineItem | RetailOrderLineItem)[] = []

  items.forEach((item) => {
    const isBundle = isGroupedInvoiceItem(item)
    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (isBundle && !isRetailOrder) {
      const bundleItem = item as InvoiceLineItem
      const children = bundleItem.items || []

      // If the bundle itself is declined, move entire bundle to declined section
      if (bundleItem.declined) {
        declined.push(bundleItem)
        return
      }

      // Separate children into active and declined
      const activeChildren = children.filter((child) => !child.declined)
      const declinedChildren = children.filter((child) => child.declined)

      // If bundle has active children, add bundle with only active children to active section
      if (activeChildren.length > 0) {
        active.push({
          ...bundleItem,
          items: activeChildren,
        })
      }

      // If bundle has declined children, add bundle with only declined children to declined section
      if (declinedChildren.length > 0) {
        declined.push({
          ...bundleItem,
          items: declinedChildren,
        })
      }
    } else if (item.declined) {
      declined.push(item)
    } else {
      active.push(item)
    }
  })

  return { activeItems: active, declinedItems: declined }
}

/**
 * Calculates drag rearrangement inputs for reordering items
 * @param localItems - Current array of items
 * @param from - Source index
 * @param to - Destination index
 * @returns Array of GroupedLineItemArrangementInput for items that need reordering
 */
export const calculateDragRearrangements = (
  localItems: (InvoiceLineItem | RetailOrderLineItem)[],
  from: number,
  to: number,
): GroupedLineItemArrangementInput[] => {
  const newItemsOrder = R.move(from, to, localItems)
  const flatLocalItems = R.chain(
    (i) =>
      !getIsRetailOrderLineItem(i) && (i as InvoiceLineItem).items
        ? [...((i as InvoiceLineItem).items || [])]
        : [i],
    localItems,
  )
  const newItemsOrderSlicedFlat = R.chain(
    (i) =>
      !getIsRetailOrderLineItem(i) && (i as InvoiceLineItem).items
        ? [...((i as InvoiceLineItem).items || [])]
        : [i],
    R.slice(0, R.max(from, to) + 1, newItemsOrder),
  )

  return newItemsOrderSlicedFlat.reduce(
    (arr: GroupedLineItemArrangementInput[], item, idx) => {
      const lineItemType = getIsRetailOrderLineItem(item)
        ? LineItemType.RetailOrderLineItem
        : LineItemType.InvoiceLineItem
      if (
        item.id &&
        (item.id !== flatLocalItems[idx]?.id || item.viewOrderNumber == null)
      ) {
        arr.push({
          lineItemId: item.id,
          lineItemType,
          viewOrderNumber: idx,
        })
      }
      return arr
    },
    [],
  )
}

/**
 * Determines if a bundle is partially declined (has declined children but bundle itself is not declined)
 * @param item - The invoice or retail order line item
 * @returns true if bundle is partially declined, false otherwise
 */
export const isPartiallyDeclinedBundle = (
  item: InvoiceLineItem | RetailOrderLineItem,
): boolean => {
  const isBundle = isGroupedInvoiceItem(item)
  const isRetailOrder = getIsRetailOrderLineItem(item)

  // Only bundles can be partially declined
  if (!isBundle || isRetailOrder) {
    return false
  }

  const bundleItem = item as InvoiceLineItem

  // Bundle is partially declined if:
  // 1. Bundle itself is not declined (declined === false)
  // 2. Bundle has declined children
  const hasDeclinedChildren =
    bundleItem.items != null &&
    bundleItem.items.length > 0 &&
    bundleItem.items.every((child) => child.declined)

  return bundleItem.declined === false && hasDeclinedChildren
}

/**
 * Handles bundle decline updates by dispatching actions for retail and invoice items
 * @param bundleItem - The bundle invoice line item
 * @param dispatch - Redux dispatch function
 * @param declined - Whether items should be declined or active
 */
export const handleBundleDeclineUpdates = (
  bundleItem: InvoiceLineItem,
  dispatch: (action: unknown) => void,
  declined: boolean,
): void => {
  if (!bundleItem.items) return

  // Separate retail and invoice items
  const retailItems: RetailOrderLineItem[] = []
  const invoiceItems: InvoiceLineItem[] = []

  bundleItem.items.forEach((childItem) => {
    if (getIsRetailOrderLineItem(childItem)) {
      retailItems.push(childItem as RetailOrderLineItem)
    } else {
      invoiceItems.push(childItem as InvoiceLineItem)
    }
  })

  // Dispatch retail order updates
  retailItems.forEach((retailChild) => {
    dispatch(
      editRetailOrderLineItem({
        id: retailChild.id,
        quantity: retailChild.quantity,
        declined,
        partNumber: retailChild.partNumber,
        expectedModificationDate: retailChild.modificationDate,
        logExpectedModificationDate: retailChild.prescription?.updatedAt,
        autoshipFrequency: retailChild.autoshipFrequency,
        autoshipUnitId: retailChild.autoshipUnit?.id,
      }),
    )
  })

  // Dispatch invoice line item updates
  if (invoiceItems.length > 0) {
    dispatch(
      editChargeSheetItem({
        updateItemInput: invoiceItems.map((invoiceChild) => ({
          id: invoiceChild.id,
          expectedModification: invoiceChild.modificationDate,
          expectedSoapLogModification: invoiceChild.soapLogModificationDate,
          declined,
        })),
      }),
    )
  }
}
